import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import useEnv from '@shared/useEnv';
import Button from '@components/shared/V2Button';
import { ValidIconTypes } from '@components/shared/CustomIcon';
import { ButtonSize } from '@components/shared/V2Button/Button';
import api from '@shared/api';

/* eslint-disable camelcase */
export type DefaultCloudinaryResponseType = {
  public_id: string;
  original_filename: string;
  format: string;
  created_at: string;
};
/* eslint-enable camelcase */

export type DefaultPhotoUploadType = {
  cloudinaryId: string;
  filename: string;
  processedAt: string;
};

const pluckFileAttributesFromCloudinary = (
  cloudinaryJson: DefaultCloudinaryResponseType,
): DefaultPhotoUploadType => {
  /* eslint-disable camelcase */
  const { public_id, original_filename, format, created_at } = cloudinaryJson;
  return {
    cloudinaryId: public_id,
    filename: `${original_filename}.${format}`,
    processedAt: created_at,
  };
  /* eslint-enable camelcase */
};

type Props<CloudinaryResponseType, PhotoUploadType> = {
  marketplaceRequest: (
    files: (PhotoUploadType | DefaultPhotoUploadType)[],
  ) => PromiseLike<{ ok: boolean }>;
  cloudinaryRoute: string;
  cloudinaryTransformer?: (
    json: CloudinaryResponseType | DefaultCloudinaryResponseType,
  ) => PhotoUploadType | DefaultPhotoUploadType;
  multiple?: boolean;
  accept: string;
  onSuccess: ({ totalFilesCount: number }) => void;
  onError: ({ totalFilesCount: number }) => void;
  buttonText: string;
  buttonIcon?: ValidIconTypes;
  buttonSize?: ButtonSize;
  buttonClass?: string;
};

const MediaUploader = <CloudinaryResponseType, PhotoUploadType>({
  onSuccess,
  onError,
  accept,
  marketplaceRequest,
  cloudinaryRoute,
  buttonText,
  multiple,
  // @ts-expect-error
  cloudinaryTransformer = pluckFileAttributesFromCloudinary,
  buttonIcon = undefined,
  buttonSize = 'medium',
  buttonClass = '',
}: Props<CloudinaryResponseType, PhotoUploadType>) => {
  const { t } = useTranslation('admin');
  const { cloudinaryUploadPreset } = useEnv();
  const [totalFilesCount, setTotalFilesCount] = useState<number>(0);
  const [successfullyUploadedFiles, setSuccessfullyUploadedFiles] = useState<
    (PhotoUploadType | DefaultPhotoUploadType)[]
  >([]);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const uploadToMarketplace = async () => {
    const marketplaceResponse = await marketplaceRequest(successfullyUploadedFiles);
    if (marketplaceResponse.ok) {
      onSuccess({ totalFilesCount });
    } else {
      onError({ totalFilesCount });
    }
  };

  const incrementUploadedFilesCount = () => {
    setUploadedFilesCount(prevCount => prevCount + 1);
  };

  useEffect(() => {
    if (uploadedFilesCount === totalFilesCount && isUploading) {
      if (successfullyUploadedFiles.length === totalFilesCount) {
        uploadToMarketplace();
      } else {
        onError({ totalFilesCount });
      }

      setUploadedFilesCount(0);
      setTotalFilesCount(0);
      setSuccessfullyUploadedFiles([]);
      setIsUploading(false);
    }
  }, [uploadedFilesCount]);

  useEffect(() => {
    if (isUploading) {
      incrementUploadedFilesCount();
    }
  }, [successfullyUploadedFiles]);

  const beforeFileUpload = async (_file: RcFile, fileList: RcFile[]) => {
    setTotalFilesCount(fileList.length);
    setIsUploading(true);
  };

  const uploadFile = async upload => {
    const formData = new FormData();
    formData.append('file', upload.file);
    formData.append('upload_preset', cloudinaryUploadPreset || '');

    const cloudinaryResponse = await api.fetch(cloudinaryRoute, { method: 'POST', body: formData });
    const cloudinaryJson = await cloudinaryResponse.json();

    if (cloudinaryResponse.ok) {
      const uploadedFile = cloudinaryTransformer(cloudinaryJson);

      setSuccessfullyUploadedFiles(prevUploadedFiles => prevUploadedFiles.concat(uploadedFile));
    } else {
      incrementUploadedFilesCount();
    }
  };

  const renderUploadingButtonText = () => {
    if (multiple) {
      return t('media.uploadProgress', {
        percentage: Math.trunc((uploadedFilesCount / totalFilesCount) * 100),
      });
    }

    return t('media.uploading');
  };

  return (
    <Upload
      accept={accept}
      beforeUpload={beforeFileUpload}
      customRequest={uploadFile}
      multiple={multiple}
      showUploadList={false}
    >
      <Button loading={isUploading} icon={buttonIcon} size={buttonSize} className={buttonClass}>
        {isUploading ? renderUploadingButtonText() : buttonText}
      </Button>
    </Upload>
  );
};

export default MediaUploader;
